<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useUserStore } from '@/stores'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

const coverUrl = computed(() => (route.query.coverUrl as string) || sessionStorage.getItem('gameCoverUrl') || '')

const loadingBgStyle = computed(() => ({
  backgroundImage: `url('${coverUrl.value || '/content/read/bg.jpg'}')`,
}))

const gameUrl = computed(() => {
  const params = new URLSearchParams()
  Object.entries(route.query).forEach(([key, value]) => {
    if (key !== 'coverUrl' && value)
      params.set(key, value as string)
  })
  return `/content/read/index.html?${params.toString()}`
})

const TIPS = [
  'Your choices shape the romance—who will win your heart?',
  'Every decision leads to a new twist in your story.',
  'Multiple endings await. Will you find true love?',
  'Swipe right or left? Your choices decide the romance.',
  'One story, countless paths. Where will your heart lead?',
  'Fall in love your way—pick your perfect partner.',
  'From first date to forever: you write the love story.',
  'Will you choose the charming stranger or the loyal friend?',
  'Every chapter holds secrets. Choose wisely.',
  'Your happy ending is just a choice away.',
  'Steamy scenes or sweet romance? You set the tone.',
  'No wrong answers, only different love stories.',
  'Replay to discover all the romantic possibilities.',
  'Love triangles, second chances, and hidden sparks.',
  'Flirt, tease, or play hard to get—it\'s up to you.',
  'Your choices change the chemistry with every character.',
  'Will you risk it all for passion or play it safe?',
  'Unlock secret scenes with the right decisions.',
  'Romance blooms in unexpected choices.',
  'The power is in your hands—write your own love saga.',
]

const iframeRef = ref<HTMLIFrameElement | null>(null)
const showLoading = ref(true)
const percent = ref(0)
const currentTip = ref('')

let tipsTimer: ReturnType<typeof setInterval> | null = null
let fallbackTimer: ReturnType<typeof setTimeout> | null = null
let mockTimer: ReturnType<typeof setInterval> | null = null

function randomTip() {
  currentTip.value = TIPS[Math.floor(Math.random() * TIPS.length)]
}

function stopTips() {
  if (tipsTimer) {
    clearInterval(tipsTimer)
    tipsTimer = null
  }
}

function stopFallback() {
  if (fallbackTimer) {
    clearTimeout(fallbackTimer)
    fallbackTimer = null
  }
}

function stopMock() {
  if (mockTimer) {
    clearInterval(mockTimer)
    mockTimer = null
  }
}

function hideLoading() {
  stopFallback()
  stopMock()
  percent.value = 100
  setTimeout(() => {
    showLoading.value = false
    stopTips()
  }, 300)
}

function onGameMessage(event: MessageEvent) {
  const { type, percent: p } = event.data ?? {}
  if (!type)
    return

  switch (type) {
    case 'unity_progress':
      percent.value = p ?? 0
      break
    case 'addressables_progress':
      percent.value = p ?? 50
      if (p >= 100)
        setTimeout(hideLoading, 500)
      break
    case 'return_to_home':
      userStore.init({ force: true })
      router.replace({ name: 'home' })
      break
    case 'navigate_external':
      if (event.data.url)
        window.location.replace(event.data.url)
      break
  }
}

function onIframeLoad() {
  try {
    const iframePath = iframeRef.value?.contentWindow?.location?.pathname ?? ''
    if (iframePath.startsWith('/content/') && !iframePath.startsWith('/content/read/')) {
      const contentId = iframePath.replace('/content/', '')
      stopTips()
      showLoading.value = false
      router.replace({ name: 'book', params: { contentId } })
    }
  }
  catch {}
}

function onPopState() {
  window.removeEventListener('popstate', onPopState)
  userStore.init({ force: true })
  router.back()
}

onMounted(() => {
  randomTip()
  tipsTimer = setInterval(randomTip, 2000)
  window.addEventListener('message', onGameMessage)

  // iframe 全屏覆盖会劫持浏览器返回事件，推入一个守卫历史记录
  // 确保用户按返回时 popstate 能传递到父窗口
  history.pushState({ gameGuard: true }, '', location.href)
  window.addEventListener('popstate', onPopState)
})

onUnmounted(() => {
  stopTips()
  stopFallback()
  stopMock()
  window.removeEventListener('message', onGameMessage)
  window.removeEventListener('popstate', onPopState)
})
</script>

<template>
  <div class="game-page">
    <Transition name="fade">
      <div v-if="showLoading" class="loading-overlay" :style="loadingBgStyle">
        <div class="spacer" />
        <div class="tips-text">
          {{ currentTip }}
        </div>
        <div class="tips-gap" />
        <div class="progress-container">
          <div class="progress-bar" :style="{ width: `${percent}%` }" />
          <div class="progress-text" :style="{ left: `${percent}%` }">
            {{ percent }}%
          </div>
        </div>
        <div class="loading-label">
          loading...
        </div>
      </div>
    </Transition>
    <iframe ref="iframeRef" :src="gameUrl" class="game-frame" allowfullscreen @load="onIframeLoad" />
  </div>
</template>

<style lang="scss" scoped>
.game-page {
  position: fixed;
  inset: 0;
  background: #231F20;
}

.game-frame {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-size: cover;
  background-position: top center;
  color: white;
  font-family: Arial, sans-serif;
}

.spacer {
  margin-top: auto;
}

.tips-text {
  max-width: 75%;
  font-size: 18px;
  font-weight: bold;
  text-align: center;
}

.tips-gap {
  margin-bottom: 3vh;
}

.progress-container {
  position: relative;
  width: 80%;
  max-width: 310px;
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 4vh;
}

.progress-bar {
  height: 14px;
  background: linear-gradient(to right, #C68BFF, #FF8DDC);
  border-radius: 20px;
  transition: width 0.2s ease;
}

.progress-text {
  position: absolute;
  top: 0;
  height: 14px;
  line-height: 14px;
  font-size: 12px;
  color: white;
  white-space: nowrap;
  transform: translateX(-110%);
  transition: left 0.2s ease;
  pointer-events: none;
}

.loading-label {
  margin-bottom: 9vh;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
